// TOLERANZ.APP brand component.
//
// Renders the Σ mark (Konzept 8 — der Summen-Vektor) optionally paired
// with the "TOLERANZ.APP" wordmark. The icon is inline SVG so it
// participates in the React render cycle (no external <img> fetch)
// and works in any layout context.
//
// API:
//   <Brand />                       icon + wordmark
//   <Brand iconOnly />              just the Σ icon
//   <Brand wordmarkOnly />          just the text
//   <Brand size="lg" />             larger overall scale
//   <Brand wordmarkClassName="text-ink" accentClassName="text-brand" />
//                                   color overrides for dark contexts
//
// The icon itself is dual-color (blue + amber) and stays the same
// across surfaces — it works on both dark and light backgrounds
// because both colors have enough contrast either way.
//
// Source of truth for the icon path data:
//   design/brand-exploration/concept-sigma.svg

const SIZES = {
  xs: { icon: 20, fontSize: '0.85rem' },
  sm: { icon: 26, fontSize: '0.95rem' },
  md: { icon: 32, fontSize: '1.15rem' },
  lg: { icon: 48, fontSize: '1.75rem' },
  xl: { icon: 72, fontSize: '2.5rem' }
};

/**
 * Just the Σ mark. Useful for places that want the icon alone —
 * mobile nav, loading states, social-share emails, OG fallback.
 *
 * Geometry source: design/brand-exploration/concept-sigma.svg
 * (the user's Inkscape design). Two paths inside the parent <g>
 * carry the Inkscape coordinate translate; cleaner than rebasing
 * the path data to a 0-relative origin and easier to diff against
 * future Inkscape edits.
 */
export function BrandMark({ size = 32, className = '', title = 'TOLERANZ.APP' }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 79.816956 79.81694"
      width={size}
      height={size}
      className={className}
      role="img"
      aria-label={title}
    >
      <title>{title}</title>
      <g transform="translate(-68.643215,87.215143)">
        {/* Blue rounded square background. Color matches the active
            --color-brand token (light: #1d51ff, dark: #7ea4ff) so the
            mark stays in step with theme switches. */}
        <path fill="var(--color-brand, #1d51ff)" d="M 82.534973,-87.215143 H 134.5684 c 7.69604,0 13.89177,5.466816 13.89177,12.257435 v 55.302078 c 0,6.79062 -6.19573,12.25743 -13.89177,12.25743 H 82.534973 c -7.696034,0 -13.891758,-5.46681 -13.891758,-12.25743 v -55.302078 c 0,-6.790619 6.195724,-12.257435 13.891758,-12.257435 z"/>
        {/* White Σ with arrow-tipped diagonals */}
        <path fill="#ffffff" d="m 84.937516,-26.950831 c 18.520834,-20.31654 1.105254,-1.21242 18.520834,-20.31654 l -18.388545,-20.3826 v -6.11502 c 15.654515,-0.0451 31.309025,-0.0901 46.963545,-0.1352 v 6.64178 l -8.79581,9.28778 2.93558,3.25088 -12.92519,2.32225 c 1.90974,-10.3415 0.43973,-2.63377 2.29013,-12.33617 l 3.1668,2.49544 c 1.54644,-1.68151 3.0929,-3.36302 4.63936,-5.04452 H 94.423996 l 17.765604,19.98197 -17.726875,19.95861 28.574795,0.10453 c 0,-0.0986 0,2.5e-4 -4.00717,-4.71211 l -3.28192,2.50243 c -2.21815,-11.635731 -0.31077,-1.63019 -2.44676,-12.83497 11.70429,2.220219 1.1252,0.21344 13.17543,2.49928 -2.81048,2.694659 -0.36314,0.34817 -3.2008,3.05966 7.76379,8.28443 0.93663,0.99945 8.88957,9.485709 v 6.389291 c -42.403294,0.121389 -1.86988,0.005 -47.228335,0.135199 z"/>
      </g>
    </svg>
  );
}

/**
 * Full lockup: Σ mark + "TOLERANZ.APP" wordmark side by side.
 *
 * For the dark app header: pass wordmarkClassName="text-ink" and
 * accentClassName="text-brand".
 * For the light landing page: defaults work.
 */
export default function Brand({
  size = 'md',
  iconOnly = false,
  wordmarkOnly = false,
  wordmarkClassName = 'text-ink',
  accentClassName = 'text-brand',
  className = ''
}) {
  const dims = SIZES[size] || SIZES.md;
  return (
    <span className={`inline-flex items-center gap-2 ${className}`}>
      {!wordmarkOnly && <BrandMark size={dims.icon} />}
      {!iconOnly && (
        <span
          className={`font-black tracking-tighter leading-none ${wordmarkClassName}`}
          style={{ fontSize: dims.fontSize }}
        >
          TOLERANZ<span className={accentClassName}>.APP</span>
        </span>
      )}
    </span>
  );
}
